import React from 'react';
import { KanbanFilterFieldSchema, DynamicFilterFieldValue, DynamicDateRangeValue, DynamicNumberRangeValue } from '../../types';
import { parseKanbanFieldOptions } from '../../utils/parseFieldOptions';
import { labelStyle, controlStyle, flexControlStyle } from './styles';

export interface DynamicFieldComponentProps {
    field: KanbanFilterFieldSchema;
    value: DynamicFilterFieldValue;
    onChange: (value: DynamicFilterFieldValue) => void;
}

const removeNonDigits = (value: string): string => {
    return value.replace(/\D/g, '');
};

const formatPhoneDisplay = (value: string): string => {
    const numbers = removeNonDigits(value);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

export const TextField: React.FC<DynamicFieldComponentProps> = ({ field, value, onChange }) => {
    const inputType = field.type === 'telefone' ? 'tel' : field.type === 'email' ? 'email' : 'text';
    const isPhone = field.type === 'telefone' || field.label.toLowerCase().includes('telefone') || field.label.toLowerCase().includes('celular');

    const displayValue = isPhone && typeof value === 'string'
        ? formatPhoneDisplay(value)
        : typeof value === 'string' ? value : '';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isPhone) {
            const numbersOnly = removeNonDigits(e.target.value);
            onChange(numbersOnly);
        } else {
            onChange(e.target.value);
        }
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>{field.label}</label>
            {isPhone && (
                <small style={{ display: 'block', color: '#666', marginBottom: '4px', fontSize: '11px' }}>
                    Digite apenas números
                </small>
            )}
            <input
                type={inputType}
                placeholder={isPhone ? "Digite apenas números" : (field.placeholder || `Filtrar por ${field.label}`)}
                value={displayValue}
                onChange={handleChange}
                style={controlStyle}
            />
        </div>
    );
};

export const NumberRangeField: React.FC<DynamicFieldComponentProps> = ({ field, value, onChange }) => {
    const currentValue = (value && typeof value === 'object' && 'min' in value)
        ? value as DynamicNumberRangeValue
        : { min: undefined, max: undefined };

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/[^\d.-]/g, '');
        const numValue = rawValue ? Number(rawValue) : undefined;
        onChange({ ...currentValue, min: numValue });
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/[^\d.-]/g, '');
        const numValue = rawValue ? Number(rawValue) : undefined;
        onChange({ ...currentValue, max: numValue });
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>{field.label}</label>
            <small style={{ display: 'block', color: '#666', marginBottom: '4px', fontSize: '11px' }}>
                Digite apenas números
            </small>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Mínimo"
                    value={currentValue.min ?? ''}
                    onChange={handleMinChange}
                    style={flexControlStyle}
                />
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Máximo"
                    value={currentValue.max ?? ''}
                    onChange={handleMaxChange}
                    style={flexControlStyle}
                />
            </div>
        </div>
    );
};

export const DateRangeField: React.FC<DynamicFieldComponentProps> = ({ field, value, onChange }) => {
    const currentValue = (value && typeof value === 'object' && 'start' in value)
        ? value as DynamicDateRangeValue
        : { start: '', end: '' };
    const inputType = field.value_type === 'datetime' ? 'datetime-local' : 'date';

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>{field.label}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                    type={inputType}
                    placeholder="Início"
                    value={currentValue.start ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange({ ...currentValue, start: e.target.value })
                    }
                    style={flexControlStyle}
                />
                <input
                    type={inputType}
                    placeholder="Fim"
                    value={currentValue.end ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange({ ...currentValue, end: e.target.value })
                    }
                    style={flexControlStyle}
                />
            </div>
        </div>
    );
};

export const SelectField: React.FC<DynamicFieldComponentProps> = ({ field, value, onChange }) => {
    const options = parseKanbanFieldOptions(field.options);

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>{field.label}</label>
            <select
                value={typeof value === 'string' ? value : ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
                style={controlStyle}
            >
                <option value="">Todos</option>
                {options.map((opt: any) => {
                    const val = typeof opt === 'string' ? opt : opt.value || opt.id || '';
                    const label = typeof opt === 'string' ? opt : opt.label || opt.name || val;
                    return (
                        <option key={`${field.id}-${val}`} value={val}>{label}</option>
                    );
                })}
            </select>
        </div>
    );
};

export const BooleanField: React.FC<DynamicFieldComponentProps> = ({ field, value, onChange }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500, fontSize: 'clamp(11px, 1.6vw, 13px)', lineHeight: 1.2 }}>
            <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
            />
            {field.label}
        </label>
    </div>
);